import {type TodayRow, type TodayRowId, type TodayTileCell, TodayTileKind} from '@/ts/types/component/today-page';

/** Read-only shape of the composition: nothing may mutate the shared configuration. */
interface TodayRowConfig {
  readonly id: TodayRowId;
  readonly cells: readonly TodayTileCell[];
}

// Object.freeze alone is shallow: it would freeze the array while leaving every row and cell
// mutable at runtime, making the "nothing may mutate" claim above hold only in TypeScript.
function deepFreezeRows(rows: readonly TodayRowConfig[]): readonly TodayRowConfig[] {
  for (const row of rows) {
    for (const cell of row.cells) {
      Object.freeze(cell);
    }
    Object.freeze(row.cells);
    Object.freeze(row);
  }
  return Object.freeze(rows);
}

/** Composition choices the page makes, which the fixed configuration below cannot express. */
interface TodayLayoutOptions {
  /**
   * The Explore tile takes its whole row, and the two content tiles fall to the line below it
   * (IPD-3234). A composition decision the page makes, not a consequence of the payload: the
   * backend always sends the content pair, so the tile never ends up alone in the row by itself.
   */
  exploreTakesRow?: boolean;
}

/**
 * The fixed Today bento composition (release-plan decision of 2026-07-07: no dynamic
 * tile reordering in v1). This constant is deliberately the ONLY place that knows which tile
 * sits in which row and with which span; the grid component just renders the rows it is
 * given. If the backend ever starts dictating the layout through the payload, this
 * constant is replaced by a payload-derived builder and nothing else changes.
 *
 * Known absence, not an oversight: the payload's `checklist` tile has no slot here. The spec
 * brought the tile back after this composition was fixed, and where its ninth cell goes is an
 * open product decision (IPD-2978's subtask 12.3, reopened). Adding it touches TodayRowId, both
 * locale files and the band heights, so it lands as its own change once product decides.
 */
export const TODAY_ROW_CONFIG: readonly TodayRowConfig[] = deepFreezeRows([
  {
    id: 'focus',
    cells: [
      {kind: TodayTileKind.upcoming, span: 'wide'},
      {kind: TodayTileKind.taskList, span: 'narrow'},
    ],
  },
  {
    id: 'discover',
    cells: [
      {kind: TodayTileKind.explore, span: 'narrow'},
      {kind: TodayTileKind.contentPrimary, span: 'narrow'},
      {kind: TodayTileKind.contentSecondary, span: 'narrow'},
    ],
  },
  {
    id: 'reflect',
    cells: [
      {kind: TodayTileKind.assessment, span: 'narrow'},
      {kind: TodayTileKind.journal, span: 'wide'},
    ],
  },
  {
    id: 'programs',
    cells: [
      {kind: TodayTileKind.programs, span: 'full'},
    ],
  },
]);

/**
 * Builds the rows to render from the fixed composition, keeping only the tiles that are
 * visible for this user. Hidden tiles produce no cell at all (the grid then reflows by
 * construction) and rows left without any visible cell are dropped entirely.
 *
 * Cells are copied rather than shared, so a caller holding the result cannot reach back
 * into the shared configuration and corrupt it for the rest of the session.
 */
export function buildTodayRows(
  visibleKinds: readonly TodayTileKind[],
  {exploreTakesRow = false}: TodayLayoutOptions = {},
): TodayRow[] {
  const visible = new Set(visibleKinds);
  return TODAY_ROW_CONFIG
    .map((row): TodayRow => ({
      id: row.id,
      cells: row.cells
        .filter((cell: TodayTileCell) => visible.has(cell.kind))
        .map((cell: TodayTileCell) => ({
          ...cell,
          span: exploreTakesRow && cell.kind === TodayTileKind.explore ? 'full' : cell.span,
        })),
    }))
    .filter((row) => row.cells.length > 0);
}
